import { el } from '@/ui/dom';
import { setBackNavModal } from '@/ui/backNav';
import { closeConfirm } from '@/ui/confirm';

/**
 * sheet.ts — the RACE CREW modal sheet (M140): the helmet picker, the invite
 * sheet, the crew-race chooser, member actions, crew settings and the link
 * prompts all open through this ONE shape, so they behave identically: a
 * gold-rimmed glass panel over a dimmed screen, a rule-flanked gold kicker
 * and a display title, Esc / the scrim / the corner ✕ close it, the edge
 * swipe-back is frozen while it is up, and focus returns where it was.
 *
 * Destructive questions still go through ui/confirm.ts (the M115 rule) —
 * opening one closes any sheet first, and vice versa.
 *
 * Only one sheet at a time: opening a second replaces the first.
 */

export interface SheetOptions {
  /** The small gold caps line over the title, e.g. 'STEP 2 OF 3'. */
  kicker?: string;
  title: string;
  /** Extra class on the panel (per-sheet layout). */
  className?: string;
  /** Called once when the sheet closes, however it closed. */
  onClose?: () => void;
  reducedMotion?: boolean;
  /** Hide the corner ✕ (a sheet that must be answered with its buttons). */
  noCloseButton?: boolean;
}

export interface SheetHandle {
  panel: HTMLDivElement;
  body: HTMLDivElement;
  close(): void;
  /** Swap the title text in place (e.g. after a rename). */
  setTitle(text: string): void;
}

let current: {
  veil: HTMLDivElement;
  onKey: (e: KeyboardEvent) => void;
  restore: Element | null;
  onClose?: () => void;
} | null = null;

export function closeSheet(): void {
  if (!current) return;
  const { veil, onKey, restore, onClose } = current;
  current = null;
  document.removeEventListener('keydown', onKey, true);
  veil.remove();
  setBackNavModal(false);
  if (restore instanceof HTMLElement && document.contains(restore)) restore.focus();
  onClose?.();
}

export function sheetOpen(): boolean {
  return current !== null;
}

export function openSheet(opts: SheetOptions): SheetHandle {
  closeSheet();
  closeConfirm();
  const restore = document.activeElement;
  const veil = el('div', 'crew-sheet-veil', document.body);
  veil.classList.toggle('reduced', !!opts.reducedMotion);
  veil.setAttribute('role', 'dialog');
  veil.setAttribute('aria-modal', 'true');

  const panel = el('div', `crew-sheet${opts.className ? ` ${opts.className}` : ''}`, veil);
  if (!opts.noCloseButton) {
    const x = el('button', 'crew-sheet-x', panel);
    x.type = 'button';
    x.setAttribute('aria-label', 'Close');
    x.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>';
    x.addEventListener('click', () => closeSheet());
  }
  if (opts.kicker) {
    const kick = el('div', 'mhead-kicker crew-sheet-kicker', panel);
    el('span', 'mhead-rule', kick);
    el('span', 'mhead-kicker-text', kick).textContent = opts.kicker;
    el('span', 'mhead-rule', kick);
  }
  const title = el('div', 'crew-sheet-title', panel);
  title.textContent = opts.title;
  title.id = `crew-sheet-title-${Date.now().toString(36)}`;
  veil.setAttribute('aria-labelledby', title.id);
  const body = el('div', 'crew-sheet-body', panel);

  veil.addEventListener('pointerdown', (e) => {
    if (e.target === veil) closeSheet();
  });
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // never let Esc ALSO close the screen behind us
      closeSheet();
    }
  };
  document.addEventListener('keydown', onKey, true);
  current = { veil, onKey, restore, onClose: opts.onClose };
  setBackNavModal(true);
  return {
    panel,
    body,
    close: () => {
      if (current?.veil === veil) closeSheet();
    },
    setTitle: (text: string) => {
      title.textContent = text;
    },
  };
}

/** A text input that never leaks keystrokes to the game's global key
 *  handlers (menus steer the attract camera / pause on some keys). */
export function sheetInput(
  parent: HTMLElement,
  opts: { className?: string; placeholder?: string; maxLength?: number; value?: string; label: string },
): HTMLInputElement {
  const input = el('input', opts.className ?? 'crew-input', parent);
  input.type = 'text';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.setAttribute('autocapitalize', 'off');
  input.setAttribute('aria-label', opts.label);
  if (opts.placeholder) input.placeholder = opts.placeholder;
  if (opts.maxLength) input.maxLength = opts.maxLength;
  if (opts.value) input.value = opts.value;
  input.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') e.stopPropagation();
  });
  input.addEventListener('keyup', (e) => e.stopPropagation());
  return input;
}
